import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import { Button } from '@/components/ui/button';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { Card, CardContent } from '@/components/ui/card';
import { Lightbulb, CircleCheck, LifeBuoy, Wand2, Clock, ShieldCheck } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Link } from 'react-router-dom';

const LEDPrinting = () => {
  const benefitsList = [
    {
      title: "Ultra-Thin & Flexible",
      description: "Works with various materials and applications.",
      icon: <Lightbulb />
    },
    {
      title: "Eye-Catching & Unique",
      description: "Enhances visibility in marketing materials and products.",
      icon: <CircleCheck />
    },
    {
      title: "Eco-Friendly & Energy-Efficient",
      description: "Designed with sustainability in mind.",
      icon: <LifeBuoy />
    }
  ];

  const whyChooseList = [
    {
      title: "Customization",
      description: "Fully tailored LED printing for any industry.",
      icon: <Wand2 />
    },
    {
      title: "Durability",
      description: "Long-lasting, reliable LED integrations.",
      icon: <ShieldCheck />
    },
    {
      title: "Seamless Integration",
      description: "Works on paper, plastic, fabric, and more.",
      icon: <Clock />
    }
  ];

  const showcaseItems = [
    {
      title: "Illuminated Business Cards",
      image: "https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Hand_holding_a_business_card_1.jpg"
    },
    {
      title: "Glowing Product Labels",
      image: "https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/glowing_led_product_label.jpeg"
    },
    {
      title: "LED-Enhanced Restaurant Menus",
      image: "https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/LED_light_menu_mockup_yesthisoneisai_but_we_really_make_these.png"
    },
    {
      title: "Wearable LED Tech in Clothing",
      image: "https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/clothing_led_light.png"
    },
    {
      title: "Light-up Stickers or Fliers",
      image: "https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/led_sticker_print.png"
    }
  ];

  const useCases = [
    {
      title: "Marketing & Branding",
      description: "Business cards, brochures, and fliers with LED highlights.",
      image: "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?q=80&w=1000&auto=format&fit=crop"
    },
    {
      title: "Retail & Packaging",
      description: "LED-enhanced labels and stickers for product differentiation.",
      image: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?q=80&w=1000&auto=format&fit=crop"
    },
    {
      title: "Hospitality & Menus",
      description: "Easy-to-read illuminated restaurant/bar menus.",
      image: "https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=1000&auto=format&fit=crop"
    },
    {
      title: "Fashion & Wearables",
      description: "Light-up clothing, shoes, and accessories.",
      image: "https://images.unsplash.com/photo-1539109136881-3be0616acf4b?q=80&w=1000&auto=format&fit=crop"
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Consultation",
      description: "Discuss your LED printing needs with our team."
    },
    {
      number: 2,
      title: "Design & Prototype",
      description: "We integrate LED technology into your desired format."
    },
    {
      number: 3,
      title: "Production & Delivery",
      description: "Your custom LED-printed products are manufactured and shipped."
    }
  ];

  const pageTitle = "LED Printing Services | Baton Rouge, LA | Astrem";
  const pageDescription = "Revolutionary LED printing services in Baton Rouge, LA. Astrem embeds ultra-thin, flexible light-emitting diodes into materials for eye-catching marketing materials. Contact us for a custom quote today!";

  return (
    <PageLayout 
      title={pageTitle}
      description={pageDescription}
      ogImage="https://astrem.co/lovable-uploads/5b79d2ca-02cf-4a09-af67-f19ff9916154.png"
      customCta="Contact us for a custom LED printing quote today!"
    >
      {/* Hero Section */}
      <PageHero 
        title="Revolutionary Printed LED Technology" 
        subtitle="Light Up Your Brand"
        description="From illuminated business cards to LED product labels, menus, and apparel, we bring your designs to life with cutting-edge, ultra-thin LED technology."
        backgroundImage="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/led_paper_thin.png"
        ctaText="Email Us to Inquire"
        ctaLink="mailto:hello@astrem.co?subject=LED Printing Inquiry"
        badge="LED Printing"
      />

      {/* What is LED Printing Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">What is LED Printing?</h2>
              <p className="text-lg text-astrem-gray-600">
                Our revolutionary LED printing technology embeds ultra-thin, flexible light-emitting diodes directly into various materials. This innovation allows for stunning illuminated designs that capture attention and create memorable brand experiences, all while maintaining the functionality and form of the original medium.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefitsList.map((benefit, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-astrem-gray-100 mb-4">
                    <div className="text-astrem-black w-8 h-8">
                      {benefit.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-medium mb-2">{benefit.title}</h3>
                  <p className="text-astrem-gray-600">{benefit.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Astrem Section */}
      <section className="section-padding bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Why Choose Astrem?</h2>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyChooseList.map((item, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <Card className="h-full border-0 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-astrem-gray-100 mb-4">
                      <div className="text-astrem-black w-8 h-8">
                        {item.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-medium mb-2">{item.title}</h3>
                    <p className="text-astrem-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Showcase Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Showcase</h2>
              <p className="text-lg text-astrem-gray-600">
                Explore the possibilities of LED printing with these sample applications.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={200}>
            <Carousel className="w-full max-w-5xl mx-auto">
              <CarouselContent>
                {showcaseItems.map((item, index) => (
                  <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                    <div className="p-2 h-full">
                      <Card className="overflow-hidden h-full border-0 shadow-md">
                        <div className="aspect-square relative overflow-hidden">
                          <img 
                            src={item.image} 
                            alt={item.title} 
                            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                          />
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-medium">{item.title}</h3>
                        </CardContent>
                      </Card>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="flex justify-center mt-6">
                <CarouselPrevious className="relative static mr-2 translate-y-0" />
                <CarouselNext className="relative static ml-2 translate-y-0" />
              </div>
            </Carousel>
          </ScrollReveal>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section-padding bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">How It Works</h2>
              <p className="text-lg text-astrem-gray-600">
                Our streamlined process makes it easy to bring your LED printing vision to life.
              </p>
            </div>
          </ScrollReveal>

          <div className="relative max-w-4xl mx-auto">
            {/* Progress bar */}
            <div className="absolute top-10 left-0 right-0 h-1 bg-astrem-gray-200 hidden md:block">
              <div className="h-full bg-astrem-black w-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <ScrollReveal key={index} delay={index * 150}>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white border-4 border-astrem-black text-2xl font-bold mb-6 z-10">
                      {step.number}
                    </div>
                    <h3 className="text-xl font-medium mb-2">{step.title}</h3>
                    <p className="text-astrem-gray-600">{step.description}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Use Cases</h2>
              <p className="text-lg text-astrem-gray-600">
                LED printing creates impact across industries and applications.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="rounded-lg overflow-hidden h-full shadow-md hover:shadow-lg transition-shadow">
                  <div className="aspect-[4/3] relative overflow-hidden">
                    <img 
                      src={useCase.image} 
                      alt={useCase.title} 
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                      <div className="p-4 text-white">
                        <h3 className="text-lg font-medium mb-1">{useCase.title}</h3>
                        <p className="text-sm text-white/80">{useCase.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-20 md:py-32">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-astrem-black/80 z-10"></div>
          <img 
            src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=1000&auto=format&fit=crop" 
            alt="LED technology" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="max-w-2xl mx-auto text-center text-white">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-4">
                Ready to Illuminate Your Brand?
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <p className="text-lg text-white/90 mb-8">
                Reach out today to explore custom LED printing solutions tailored to your needs.
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full">
                <Link to="/contact">
                  Contact Us to Inquire
                </Link>
              </Button>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default LEDPrinting;
